import { FileRepository } from '~/repositories/fileRepository';
import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { validateId } from '~/repositories/utils';
import { urlWithQuery } from '~/utils/url';
import type { UploadFileOptions } from '~/repositories/uploadSessionRepository';
import type {
  AddAssociationViolationFilesDto,
  AddAssociationViolationLeaseBillDto,
  AssociationViolationDto,
  AssociationViolationStatus,
  CreateAssociationViolationRequestDto,
  CreateWorkOrderFromAssociationViolationDto,
  HydratedWorkOrderDto,
  PaginatedAssociationViolationListDto,
  SendAssociationViolationNoticeDto,
  UpdateAssociationViolationRequestDto,
  PortalStatus,
} from '~/swagger/portal/Api';

export type Filters = {
  disable?: boolean;
  page?: number;
  pageSize?: number;
  skip?: number;
  active?: boolean;
  firstName?: string;
  lastName?: string;
  firstOrLastName?: string;
  phone?: string;
  email?: string;
  searchText?: string;
  portalStatus?: PortalStatus;
};

export const VIOLATION_BASE_URL = '/api/associations/violations';
export class ViolationsRepository extends FileRepository {
  public async list(filter: Filters): Promise<PaginatedAssociationViolationListDto> {
    const url = urlWithQuery(VIOLATION_BASE_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async add(payload: CreateAssociationViolationRequestDto): Promise<AssociationViolationDto> {
    const url = `${VIOLATION_BASE_URL}`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async get(id: string): Promise<AssociationViolationDto> {
    const url = `${VIOLATION_BASE_URL}/${id}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async update(id: string, payload: UpdateAssociationViolationRequestDto): Promise<AssociationViolationDto> {
    const url = `${VIOLATION_BASE_URL}/${id}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async delete(id: string): Promise<void> {
    const url = `${VIOLATION_BASE_URL}/${id}`;
    await this.fetchWithAuth(url, {
      method: 'DELETE',
      body: JSON.stringify({ deleteBills: true }),
    });
  }

  public async uploadViolationFiles(violationId: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    validateId(violationId, 'Violation');
    if (files.length === 0) return [];

    const sessionUrl = `${VIOLATION_BASE_URL}/${violationId}/files/upload-sessions`;
    const uploadSessionIds = await uploadSessionRepository.uploadFiles(sessionUrl, files, opts);

    const url = `${VIOLATION_BASE_URL}/${violationId}/files`;
    const payload: AddAssociationViolationFilesDto = {
      uploadSessionIds,
    };
    await this.fetchWithAuth(url, { method: 'POST', body: JSON.stringify(payload) });

    return uploadSessionIds;
  }

  public async deleteViolationFile(violationId: string, fileId: string): Promise<void> {
    validateId(violationId, 'Violation');
    validateId(fileId, 'File');

    await this.fetchWithAuth(`${VIOLATION_BASE_URL}/${violationId}/files/${fileId}`, { method: 'DELETE' });
  }

  public async createFineBill(
    id: string,
    data: {
      dueDate: string;
    }
  ): Promise<AssociationViolationDto> {
    const url = `${VIOLATION_BASE_URL}/${id}/fine`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.getJsonResponse(response);
  }

  public async changeStatus(id: string, status: AssociationViolationStatus): Promise<AssociationViolationDto> {
    const url = `${VIOLATION_BASE_URL}/${id}/status`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    return this.getJsonResponse(response);
  }

  public async createWorkOrder(id: string, data: CreateWorkOrderFromAssociationViolationDto): Promise<HydratedWorkOrderDto> {
    const url = `${VIOLATION_BASE_URL}/${id}/work-order`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.getJsonResponse(response);
  }

  public async createLeaseBill(id: string, data: AddAssociationViolationLeaseBillDto): Promise<AssociationViolationDto> {
    const url = `${VIOLATION_BASE_URL}/${id}/lease-bill`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return this.getJsonResponse(response);
  }
  public async uploadViolationNotificationAttachments(id: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    validateId(id, 'Violation');
    const sessionUrl = `${VIOLATION_BASE_URL}/${id}/notification/upload-sessions`;
    return await uploadSessionRepository.uploadFiles(sessionUrl, files, opts);
  }

  public async createOwnerNotification(id: string, payload: SendAssociationViolationNoticeDto): Promise<HydratedWorkOrderDto> {
    const url = `${VIOLATION_BASE_URL}/${id}/notification/owner`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async createLeaseNotification(id: string, payload: SendAssociationViolationNoticeDto): Promise<HydratedWorkOrderDto> {
    const url = `${VIOLATION_BASE_URL}/${id}/notification/lease`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }
}
